/**
 * SiteFooterV2 — footer public v2, partagé.
 *
 * Extrait tel quel de HomeV2.jsx (markup, styles inline, classe
 * `hv2-flink`) : rien n'a été réécrit, seulement déplacé.
 *
 * Seule adaptation, requise pour que le footer serve hors de la home :
 * les deux entrées « #compare » et « #cases » visent des sections de la
 * HOME. Sur la home, elles restent des ancres natives, au comportement
 * strictement identique à avant. Ailleurs, elles pointent « /#compare »
 * et on fait le saut à la main.
 *
 * Pourquoi à la main : laisser le lien natif charger « /#cases » ne
 * marche pas. Le navigateur cherche l'ancre à l'analyse du document,
 * quand React n'a encore rien rendu — la section n'existe pas, le saut
 * n'a jamais lieu et on reste en haut de la home. On navigue donc via le
 * router puis on attend que la section apparaisse pour la rejoindre.
 * (Les sections portent un `scrollMarginTop` qui dégage la nav fixe.)
 */
import React, { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconShield, IconLock, IconBadgeCheck } from './icons';
import { footerColsFor, MANAGE_COOKIES_HREF } from './routes';
import { useSiteLanguage } from '../../hooks/useSiteLanguage';
import { resetConsent } from '../../lib/consent';
import './siteV2.css';

// Copie du footer, dans les trois langues de la barre. Rien n'est laissé
// en français quand le site est en NL ou en EN : un pied de page à moitié
// traduit est le premier endroit où l'on cesse de croire au reste.
const COPY = {
  fr: {
    // Manifeste de marque — formulation FIGÉE en français, identique au
    // footer de /entreprises. Les versions NL et EN en sont la traduction
    // fidèle ; la version française, elle, ne se reformule pas.
    manifesto: 'Le système juridique est cassé. On l’a reconstruit · une IA ancrée dans le droit, des humains qui se battent pour toi.',
    trustHumans: 'De vrais juristes & avocats partenaires',
    trustPrivate: 'Tes documents restent privés',
    // Le pied de page est sur TOUTES les pages, y compris /plans où le
    // cycle annuel — sélectionné par défaut — engage douze mois. La
    // promesse absolue y était donc fausse à côté de la carte qui
    // affichait « engagement 12 mois ». Elle nomme maintenant son cycle.
    trustNoCommit: 'Mensuel sans engagement · annulable en deux clics',
    legal: 'Archer fournit de l’information juridique et de l’analyse de documents, pas du conseil juridique. Pour les affaires nécessitant une représentation devant un tribunal, Archer te met en relation avec un avocat partenaire.',
    rights: '© 2026 Archer. Tous droits réservés.',
    country: '🇧🇪 Belgique',
  },
  nl: {
    manifesto: 'Het rechtssysteem is stuk. Wij hebben het opnieuw gebouwd · een AI verankerd in het recht, mensen die voor jou vechten.',
    trustHumans: 'Echte juristen & partneradvocaten',
    trustPrivate: 'Jouw documenten blijven privé',
    trustNoCommit: 'Maandelijks zonder verbintenis · opzegbaar in twee kliks',
    legal: 'Archer levert juridische informatie en documentanalyse, geen juridisch advies. Voor zaken die vertegenwoordiging voor de rechtbank vereisen, brengt Archer je in contact met een partneradvocaat.',
    rights: '© 2026 Archer. Alle rechten voorbehouden.',
    country: '🇧🇪 België',
  },
  en: {
    manifesto: 'The legal system is broken. We rebuilt it · an AI grounded in the law, humans who fight for you.',
    trustHumans: 'Real legal experts & partner lawyers',
    trustPrivate: 'Your documents stay private',
    trustNoCommit: 'Monthly with no commitment · cancel in two clicks',
    legal: 'Archer provides legal information and document analysis, not legal advice. For matters requiring representation in court, Archer connects you with a partner lawyer.',
    rights: '© 2026 Archer. All rights reserved.',
    country: '🇧🇪 Belgium',
  },
};

export default function SiteFooterV2() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onHome = pathname === '/';
  // La lecture de `archer_locale` et son abonnement vivaient ici, en
  // double du header. Ils sont désormais dans `useSiteLanguage`, partagé
  // par tout le chrome public et par le corps des pages.
  const [lang] = useSiteLanguage();
  const t = COPY[lang] || COPY.fr;
  const cols = footerColsFor(lang);

  // Saut vers une section de la home depuis une autre page.
  //
  // Ce handler ne fait plus QUE router côté client — sans lui, ces liens
  // sont des `<a href="/#section">` et le navigateur rechargerait toute
  // l'application. La partie qui guettait l'apparition de la section pour
  // la faire défiler est partie : `ScrollToTop`, monté au niveau du
  // routeur, traite désormais les ancres pour tout le site. Garder les
  // deux, c'était deux défilements concurrents sur le même élément — et
  // celui d'ici héritait du `scroll-behavior: smooth` global, donc une
  // animation par-dessus le placement instantané de l'autre.
  // « Gérer mes cookies » — le seul lien du footer qui ne mène nulle part.
  // Il efface le choix enregistré ; `resetConsent` émet l'événement que
  // CookieBanner écoute, donc le bandeau se rouvre immédiatement, sur
  // place, sans rechargement ni navigation. Le visiteur retrouve les deux
  // mêmes boutons qu'à sa première visite.
  const openCookieBanner = useCallback((e) => {
    e.preventDefault();
    resetConsent();
    // Le bandeau s'ancre en bas de l'écran : on le laisse apparaître là
    // où le visiteur est (il vient de cliquer dans le pied de page).
  }, []);

  const goHomeAnchor = useCallback((e, hash) => {
    // On laisse filer les clics « ouvrir dans un onglet » : ils doivent
    // garder le comportement natif du href.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    navigate({ pathname: '/', hash });
  }, [navigate]);

  return (
    <footer className="sv2-footer" data-testid="site-footer-v2" style={{ background: 'linear-gradient(160deg,#1a56db 0%,#12308a 100%)', borderTop: 'none', padding: '64px 40px 28px' }}>
      <div style={{ maxWidth: 1160, margin: '0 auto' }}>
        <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap', paddingBottom: 44, borderBottom: '1px solid rgba(255,255,255,.16)' }}>
          <div style={{ flex: '1 1 280px', minWidth: 240 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 21, fontWeight: 700, letterSpacing: '-.025em', color: '#fff', marginBottom: 12 }}>Archer</div>
            <p style={{ fontSize: 14, fontWeight: 400, lineHeight: 1.6, color: 'rgba(255,255,255,.78)', margin: '0 0 18px', maxWidth: 280 }}>
              {t.manifesto}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12.5, fontWeight: 500, color: 'rgba(255,255,255,.85)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><IconShield check size={14} stroke="#4ade80" width={2} />{t.trustHumans}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><IconLock />{t.trustPrivate}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><IconBadgeCheck />{t.trustNoCommit}</span>
            </div>
          </div>

          {cols.map((col) => (
            <div key={col.title} style={{ flex: '1 1 140px', minWidth: 130 }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,.5)', marginBottom: 14 }}>{col.title}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13.5, fontWeight: 500 }}>
                {col.links.map(([label, href]) => {
                  // Sentinelle « Gérer mes cookies » : traitée AVANT la
                  // logique d'ancre, car elle commence aussi par « # »
                  // sans viser une section de la home.
                  if (href === MANAGE_COOKIES_HREF) {
                    return (
                      <a
                        key={label} className="hv2-flink"
                        href={href}
                        onClick={openCookieBanner}
                        data-testid="footer-manage-cookies"
                        style={{ color: 'rgba(255,255,255,.78)' }}
                      >
                        {label}
                      </a>
                    );
                  }
                  const isAnchor = href.startsWith('#');
                  // Hors de la home, une ancre de la home devient un lien
                  // absolu vers la home + ancre.
                  const awayAnchor = isAnchor && !onHome;
                  const target = awayAnchor ? `/${href}` : href;
                  return (
                    <a
                      key={label} className="hv2-flink"
                      href={target}
                      // Sur la home, les ancres (#…) restent natives —
                      // comportement inchangé. Ailleurs, saut assisté.
                      // Les vraies routes passent par le router pour
                      // éviter un rechargement.
                      onClick={
                        awayAnchor ? (e) => goHomeAnchor(e, href)
                          : isAnchor ? undefined
                            : (e) => { e.preventDefault(); navigate(href); }
                      }
                      style={{ color: 'rgba(255,255,255,.78)' }}
                    >
                      {label}
                    </a>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <p style={{ fontSize: 12, fontWeight: 400, lineHeight: 1.6, color: 'rgba(255,255,255,.55)', margin: '20px 0 0', maxWidth: 760 }}>
          {t.legal}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginTop: 16 }}>
          <div style={{ fontSize: 12.5, fontWeight: 400, color: 'rgba(255,255,255,.55)' }}>{t.rights}</div>
          {/* Les icônes réseaux de la référence pointaient toutes sur "#" :
              retirées tant qu'on n'a pas les vraies URL de profils. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12.5, fontWeight: 500, color: 'rgba(255,255,255,.85)' }}>
            <span>{t.country}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
